import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GoPlayIcon, NoEventsIcon } from "../../assets/icons/MUI/ExploreIcons";
import EventCard, { EventCardLoading } from "../Events/EventsList/EventCard";
import { useProfileData } from "../../hooks/useProfileData";
import { useQueryEventsListing } from "../../api/queries/explore/useQueryGoPlay";
import APP_ROUTES from "../../routers/routeConstants";
import { colors } from "../../theme/palette";

import { GoEvent } from "../../types";
import EventsListCalendar from "../Events/EventsList/EventsListCalendar";

const GoPlay = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [calendarTab, setCalendarTab] = useState<string>("");
  const [minDate, setMinDate] = useState<string>("");
  const [maxDate, setMaxDate] = useState<string>("");

  const handleChangeCalendarValue = (newValue: string) => {
    setCalendarTab(newValue);
  };

  const { data: userData } = useProfileData();
  const userId = userData.id;

  const { eventsListingResult, isEventsListingResultLoading } = useQueryEventsListing(minDate, maxDate);
  const eventsListing = eventsListingResult?.items.map((post: any) => post.event) || [];

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid #EAECF0",
        background: colors.White,
        height: "fit-content",
        minHeight: "402px",
        padding: "18px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: "10px",
          marginBottom: "27px",
        }}
      >
        <GoPlayIcon />
        <Typography
          sx={{
            color: colors.Black100,
            fontFamily: "Montserrat",
            fontSize: "18px",
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "normal",
          }}
        >
          {t("pages.explore.goPlay")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <EventsListCalendar
            calendarTab={calendarTab}
            handleChangeCalendarValue={handleChangeCalendarValue}
            setMinDate={setMinDate}
            setMaxDate={setMaxDate}
            isLoading={false}
          />
          <Link
            onClick={() => navigate(APP_ROUTES.EVENTS)}
            sx={{
              color: colors.Green100,
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "24px",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {t("pages.explore.goToEventPage")}
          </Link>
        </Box>
        <Box
          sx={{
            display: "flex",
            columnGap: "16px",
            overflowX: "auto",
            width: "100%",
            paddingBottom: "10px",
          }}
        >
          {isEventsListingResultLoading ? (
            [...Array(10)].map((_, idx) => (
              <Box key={idx} sx={{ minWidth: "394px" }}>
                <EventCardLoading />
              </Box>
            ))
          ) : (
            <></>
          )}
          {eventsListing.length === 0 && !isEventsListingResultLoading && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                rowGap: "11px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <NoEventsIcon />
              <Typography
                sx={{
                  color: colors.Dark600,
                  textAlign: "center",
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                {t("pages.events.eventList.noEvents")}
              </Typography>
            </Box>
          )}
          {eventsListing.length > 0 &&
            !isEventsListingResultLoading &&
            eventsListing.map((event: GoEvent) => (
              <Box key={event.id} sx={{ minWidth: "394px" }}>
                <EventCard key={event.id} event={event} userId={userId} />
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default GoPlay;
